/**
 * Manages the "Manage Mortgage" button + iframe modal flow.
 *
 * Behavior:
 * - Click "Manage Mortgage" → AJAX call to `urlPath` to retrieve a fresh MAI
 *   single-use redirect URL.
 * - On success, opens a modal embedding MAI in an iframe.
 * - The modal always exposes an "Open in new tab" link as a fallback in case
 *   the iframe is blocked by MAI's `frame-ancestors` CSP. That fallback is
 *   reliable; auto-detection of CSP blocking is unreliable across browsers
 *   because the iframe is cross-origin and does not consistently report load
 *   failures.
 * - On AJAX failure, redirects to `fallbackPath` which surfaces a flash error.
 *
 * PostHog events (gated on `window.posthog` being present):
 * - `clubhouse_mortgage_card_viewed`   — card rendered on the payments page
 * - `clubhouse_mortgage_manage_clicked` — "Manage Mortgage" clicked
 * - `clubhouse_mortgage_portal_opened`  — MAI loaded in the iframe (success)
 * - `clubhouse_mortgage_opened_new_tab` — "Open in new tab" fallback clicked
 * - `clubhouse_mortgage_load_failed`    — AJAX failed before the iframe opened
 */
export default function mortgageManager(config = {}) {
    const { urlPath, fallbackPath, contractId, propertyId } = config;

    const track = (event, extra = {}) => {
        window.posthog?.capture(event, {
            contract_id: contractId,
            property_id: propertyId,
            ...extra,
        });
    };

    return {
        isOpen: false,
        loading: false,
        iframeUrl: '',

        init() {
            track('clubhouse_mortgage_card_viewed');
        },

        async open() {
            track('clubhouse_mortgage_manage_clicked');

            this.iframeUrl = '';
            this.loading = true;
            this.isOpen = true;

            try {
                const response = await fetch(urlPath, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const { url } = await response.json();
                this.iframeUrl = url;
                this.loading = false;

                track('clubhouse_mortgage_portal_opened', { method: 'iframe' });
            } catch (e) {
                this.loading = false;
                this.isOpen = false;

                // Keep `reason` to the two documented buckets: an HTTP status
                // (thrown above as `HTTP <status>`) or `network_error` for
                // everything else (fetch TypeError, JSON SyntaxError, …), whose
                // messages are browser-specific and would otherwise fragment
                // the funnel.
                const reason = e.message && e.message.startsWith('HTTP ')
                    ? e.message
                    : 'network_error';

                track('clubhouse_mortgage_load_failed', { reason });

                window.location.href = fallbackPath;
            }
        },

        openInNewTab() {
            track('clubhouse_mortgage_opened_new_tab');
        },

        close() {
            this.isOpen = false;
            this.loading = false;
            this.iframeUrl = '';
        },
    };
}
